import { useEffect, useState } from "react";
import { Gift, Copy, Check, Users, Loader2 } from "lucide-react";
import api, { apiErr, fmtCredits } from "../lib/api";
import { Card } from "./ui-bits";
import { toast } from "sonner";

/**
 * Panel de referidos del usuario: su enlace, a quién ha traído y cuánto ha ganado.
 *
 * El premio se paga cuando el referido CONTRATA, nunca cuando se registra — por eso hay dos
 * cifras distintas y separadas: "ganado" (los que ya pagaron) y "pendientes" (los que entraron
 * por el enlace y todavía no). Esa segunda cifra es la que dice si el enlace funciona pero la
 * conversión no, y esconderla haría creer que el enlace no sirve.
 *
 * Los correos llegan ya recortados del servidor (ver _email_parcial): quien refiere ve a quién
 * trajo sin ver la dirección entera de alguien que quizá no sabe que figura en el panel de otro.
 */
export default function Referidos() {
  const [d, setD] = useState(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    api.get("/referidos").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!d) {
    return (
      <Card className="p-6 flex justify-center">
        <Loader2 className="animate-spin text-blue-600" size={28} />
      </Card>
    );
  }

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(d.enlace);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin permiso de portapapeles (http, navegador antiguo): el enlace está a la vista y se
      // puede seleccionar a mano, así que no se molesta al usuario con un error.
      toast.info("Copia el enlace manualmente");
    }
  };

  const planes = [
    ["starter", "Starter"], ["creator", "Creator"], ["pro", "Pro"], ["studio", "Studio"],
  ].filter(([k]) => (d.premios || {})[k] > 0);

  return (
    <Card className="p-6 space-y-5" data-testid="referidos-usuario">
      <div>
        <h3 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Gift size={22} className="text-emerald-600" /> Invita y gana créditos
        </h3>
        <p className="text-base text-slate-600 mt-1">
          Comparte tu enlace. Cuando alguien contrata un plan, os llevamos créditos a los dos: a ti
          según el plan que elija, y a él {fmtCredits(d.bienvenida)} de bienvenida.
        </p>
      </div>

      <div>
        <label className="block text-base font-semibold text-slate-800 mb-2">Tu enlace</label>
        <div className="flex gap-2">
          <input readOnly value={d.enlace} data-testid="referidos-enlace"
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 border-2 border-slate-300 rounded-lg px-4 py-3 text-base bg-slate-50 text-slate-700" />
          <button onClick={copiar} data-testid="referidos-copiar"
            className={`shrink-0 inline-flex items-center gap-2 rounded-lg px-4 font-bold text-base transition-colors ${
              copiado ? "bg-emerald-600 text-white" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
            {copiado ? <><Check size={18} /> Copiado</> : <><Copy size={18} /> Copiar</>}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-2xl font-extrabold text-emerald-900 tabular-nums">{fmtCredits(d.ganado)}</div>
          <div className="text-sm font-semibold text-emerald-700">créditos ganados</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{d.pendientes}</div>
          <div className="text-sm font-semibold text-slate-600">
            registrados sin contratar
          </div>
        </div>
      </div>

      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">Lo que ganas según el plan que contrate</div>
        <div className="flex flex-wrap gap-2">
          {planes.map(([k, label]) => (
            <span key={k} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm">
              <b className="text-emerald-900">{label}</b>
              <span className="text-emerald-700">+{fmtCredits(d.premios[k])}</span>
            </span>
          ))}
        </div>
      </div>

      {d.referidos?.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
            <Users size={16} /> A quién has traído
          </div>
          <div className="space-y-1.5">
            {d.referidos.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-base border-b border-slate-100 pb-1.5">
                <span className="text-slate-800">{r.email}</span>
                <span className="text-slate-400 text-sm">{r.plan}</span>
                <span className="ml-auto font-bold text-emerald-700 tabular-nums">+{fmtCredits(r.creditos)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
